// @flow
import * as yup from 'yup';
import moment from 'moment';
import { constants } from '@clubmed/utils';

import { t } from 'Src/i18n';

const { EVENT_TYPES, USER_SALUTATIONS } = constants;

export const getInitialFormValues = () => ({
  type: '',
  numberOfParticipants: '',
  withMeetingRooms: false,
  withRooms: false,
  withServices: false,
  withTransport: false,
  logisticsComment: '',
  region: '',
  resort: '',
  dates: { from: null, to: null },
  withFlexibleDates: false,
  company: '',
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  postcode: '',
  city: '',
  country: '',
  generalComment: '',
  optinEmail: false,
  optinSMS: false,
  optinPhone: false,
});

type EventFormValues = {
  address: string,
  city: string,
  company: string,
  country: string,
  dates: { from: Date, to: Date },
  email: string,
  firstName: string,
  generalComment: string,
  lastName: string,
  logisticsComment: string,
  numberOfParticipants: number,
  optinEmail: boolean,
  optinPhone: boolean,
  optinSMS: boolean,
  phone: string,
  postcode: string,
  resort: string,
  salutation: string,
  type: string,
  withFlexibleDates: boolean,
  withMeetingRooms: boolean,
  withTransport: boolean,
};

export const getEventQuery = (values: EventFormValues) => {
  const {
    address,
    city,
    company,
    country,
    dates,
    email,
    firstName,
    generalComment,
    lastName,
    logisticsComment,
    numberOfParticipants,
    optinEmail,
    optinPhone,
    optinSMS,
    phone,
    postcode,
    resort,
    salutation,
    type,
    withFlexibleDates,
    withMeetingRooms,
    withTransport,
  } = values;
  const departureDate = dates.from ? `"${moment(dates.from).format('YYYY-MM-DD')}"` : 'null';
  const returnDate = dates.to ? `"${moment(dates.to).format('YYYY-MM-DD')}"` : 'null';
  return `mutation {
    event(eventInput: {
      address: "${address}",
      city: "${city}",
      company: "${company}",
      country: "${country}",
      departureDate: ${departureDate},
      email: "${email}",
      firstName: "${firstName}",
      generalComment: "${generalComment}",
      lastName: "${lastName}",
			logisticsComment: "${logisticsComment}",
      numberOfParticipants: ${numberOfParticipants},
      optinEmail: ${optinEmail.toString()},
      optinPhone: ${optinPhone.toString()},
      optinSMS: ${optinSMS.toString()},
      phone: "${phone}",
      postcode: "${postcode}",
      resort: "${resort}",
      returnDate: ${returnDate},
      salutation: ${salutation},
      type: ${type},
      withFlexibleDates: ${withFlexibleDates.toString()},
      withMeetingRooms: ${withMeetingRooms.toString()},
      withTransport: ${withTransport.toString()},
    }) {
      __typename
      ...on EventError {
        statusCode
        statusMessage
        errorCode
        errorDescription
      }
      ...on Event {
        id
      }
    }
  }`;
};

export const getEventSchema = (resortIds: Array<string>) =>
  yup.object().shape({
    type: yup
      .mixed()
      .oneOf(Object.keys(EVENT_TYPES))
      .required(t('eventForm.packageSection.eventTypePlaceholder')),
    numberOfParticipants: yup
      .number()
      .required(t('eventForm.packageSection.numberOfParticipantsPlaceholder')),
    withFlexibleDates: yup.boolean(),
    withMeetingRooms: yup.boolean(),
    withRooms: yup.boolean(),
    withServices: yup.boolean(),
    withTransport: yup.boolean(),
    logisticsComment: yup.string(),
    resort: yup.mixed().oneOf(resortIds),
    dates: yup.object().shape({
      from: yup.date().nullable(),
      to: yup.date().nullable(),
    }),
    company: yup.string(),
    salutation: yup
      .mixed()
      .oneOf(Object.keys(USER_SALUTATIONS))
      .required(t('eventForm.userSection.salutationPlaceholder')),
    firstName: yup.string().required(t('eventForm.userSection.firstNamePlaceholder')),
    lastName: yup.string().required(t('eventForm.userSection.lastNamePlaceholder')),
    email: yup
      .string()
      .email(t('eventForm.userSection.emailPlaceholder'))
      .required(t('eventForm.userSection.emailPlaceholder')),
    phone: yup.string().required(t('eventForm.userSection.phonePlaceholder')),
    address: yup.string().required(t('eventForm.userSection.streetPlaceholder')),
    postcode: yup.string().required(t('eventForm.userSection.postcodePlaceholder')),
    city: yup.string().required(t('eventForm.userSection.cityPlaceholder')),
    country: yup.string().required(t('eventForm.userSection.countryPlaceholder')),
    generalComment: yup.string(),
    optinEmail: yup.boolean(),
    optinSMS: yup.boolean(),
    optinPhone: yup.boolean(),
  });
